import { useEffect, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Spinner, alert } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import { useRootStore } from '@/lib/providers/RootProvider';
import { salesOrderPickPath } from '@/lib/pickVerifyShip';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import SalesOrderVerify from './SalesOrderVerify';
import {
  VERIFY_ORDER_NOT_FOUND_MESSAGE,
  classifyVerifyOrderRefusal,
  verifyLostOrderMessage,
  verifyScreenFor
} from './verifyScreen';

/**
 * Pick → Verify → Ship (Option 2) — the Verify screen's data. Everything the screen shows and every
 * routing decision it makes comes from `salesOrderVerifyList`, a PRIMARY read in this page's own
 * network-only document (the order's stamp decides, never the root store). Every Verify mutation
 * returns the whole list again, so the screen replaces its state from each answer instead of
 * refetching.
 */

// Keep in step across the query and the four mutations that return a list: a field selected in
// one and not another comes back undefined after the first scan and the row quietly loses it.
const VERIFY_LIST_FIELDS = gql`
  fragment SalesOrderVerifyListFields on SalesOrderVerifyList {
    salesOrderId
    vendorSalesOrderNumber
    vendorStatus
    clientName
    toteName
    workflow {
      workflow
      stage
      started
      verifiedAt
      verifiedByName
    }
    lines {
      key
      orderItemIds
      productName
      ndc
      upc
      deaSchedule
      kind
      required
      scanned
      serialNumbers
      # The GTIN middle-12 values the server matches this group on — what lets the screen tell a
      # pack still in frame, a duplicate serial, a bulk bottle or an over-scan apart before any
      # round trip, and count the unit on its row the moment it is read.
      matchKeys
      orphan
      # Controlled (CI–CV) products this user may not verify: listed and counted, never scanned.
      controlled
      controlledLocked
      pickZone {
        id
        name
      }
      pickZones {
        id
        name
      }
      # The stock balances a BULK / UPC group takes units from (and puts them back to) — what the
      # lot chooser offers, so each unit is recorded against the lot really in hand. [] for
      # SERIALIZED groups, whose serial already names the unit.
      lots {
        balanceId
        zone {
          id
          name
        }
        lot
        expiration
        available
        verified
        suggested
      }
    }
    totalRequired
    totalScanned
    complete
    overScanned
    controlledLockedLineCount
  }
`;

export const SALES_ORDER_VERIFY_LIST = gql`
  ${VERIFY_LIST_FIELDS}
  query SalesOrderVerifyList($companyId: ID!, $salesOrderId: ID!) {
    salesOrderVerifyList(companyId: $companyId, salesOrderId: $salesOrderId) {
      ...SalesOrderVerifyListFields
    }
    # The per-page company read every Option-2 screen makes (design §7). Verify itself routes on
    # the ORDER's stamp — a started order finishes in Option 2 even if the company switched back —
    # so this only feeds the "switched back" note. \`id\` is REQUIRED so Apollo normalizes it to
    # Company:<id> rather than caching it inline.
    company(id: $companyId) {
      id
      fulfillmentWorkflow
    }
    # Customer Pickup (@/lib/customerPickup): labelled in the header, and once verified it skips the
    # Shipping step — the server sets it to Customer Pickup when Verify completes.
    salesOrder(id: $salesOrderId, vendorId: $companyId) {
      id
      customerPickup {
        enabled
        markedAt
        lastError
      }
    }
  }
`;

const SALES_ORDER_VERIFY_SCAN = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyScan(
    $companyId: ID!
    $salesOrderId: ID!
    $unit: PickVerifyUnitInput!
  ) {
    salesOrderVerifyScan(
      companyId: $companyId
      salesOrderId: $salesOrderId
      unit: $unit
    ) {
      lineKey
      addedQuantity
      removedQuantity
      list {
        ...SalesOrderVerifyListFields
      }
    }
  }
`;

const SALES_ORDER_VERIFY_REMOVE = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyRemove(
    $companyId: ID!
    $salesOrderId: ID!
    $unit: PickVerifyUnitInput!
  ) {
    salesOrderVerifyRemove(
      companyId: $companyId
      salesOrderId: $salesOrderId
      unit: $unit
    ) {
      lineKey
      addedQuantity
      removedQuantity
      list {
        ...SalesOrderVerifyListFields
      }
    }
  }
`;

// The same two writes, sent with the device's id for the read (`clientScanId`): the server records it
// with the write, so a request re-sent after a timeout is answered instead of applied twice. The two
// documents above stay for a server that predates the argument (mid-deploy) — see verifySend.ts.
const SALES_ORDER_VERIFY_SCAN_WITH_ID = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyScanWithId(
    $companyId: ID!
    $salesOrderId: ID!
    $unit: PickVerifyUnitInput!
    $clientScanId: String
  ) {
    salesOrderVerifyScan(
      companyId: $companyId
      salesOrderId: $salesOrderId
      unit: $unit
      clientScanId: $clientScanId
    ) {
      lineKey
      addedQuantity
      removedQuantity
      list {
        ...SalesOrderVerifyListFields
      }
    }
  }
`;

const SALES_ORDER_VERIFY_REMOVE_WITH_ID = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyRemoveWithId(
    $companyId: ID!
    $salesOrderId: ID!
    $unit: PickVerifyUnitInput!
    $clientScanId: String
  ) {
    salesOrderVerifyRemove(
      companyId: $companyId
      salesOrderId: $salesOrderId
      unit: $unit
      clientScanId: $clientScanId
    ) {
      lineKey
      addedQuantity
      removedQuantity
      list {
        ...SalesOrderVerifyListFields
      }
    }
  }
`;

// The Adds queued on the device, recorded in one transaction: one result per unit (matched back to
// its read by clientScanId) and ONE list, built once for the whole batch.
const SALES_ORDER_VERIFY_SCAN_BATCH = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyScanBatch(
    $companyId: ID!
    $salesOrderId: ID!
    $units: [SalesOrderVerifyScanBatchUnitInput!]!
  ) {
    salesOrderVerifyScanBatch(
      companyId: $companyId
      salesOrderId: $salesOrderId
      units: $units
    ) {
      results {
        clientScanId
        status
        lineKey
        addedQuantity
        replayed
        error {
          message
          code
          reason
        }
      }
      list {
        ...SalesOrderVerifyListFields
      }
    }
  }
`;

// A scan refused because its unit is in quarantine (extensions.reason VERIFY_UNIT_QUARANTINED): what
// the stock prompt offers. Network-only — it must describe the stock as it is now.
export const SALES_ORDER_VERIFY_QUARANTINE = gql`
  query SalesOrderVerifyQuarantine(
    $companyId: ID!
    $salesOrderId: ID!
    $unit: PickVerifyUnitInput!
  ) {
    salesOrderVerifyQuarantine(
      companyId: $companyId
      salesOrderId: $salesOrderId
      unit: $unit
    ) {
      lineKey
      productName
      kind
      reason
      serialNumber
      quantity
      remaining
      canStock
      options {
        balanceId
        locationId
        lot
        expiration
        quarantined
        zone {
          id
          name
          zoneType
        }
        suggestedStockZone {
          id
          name
          zoneType
        }
      }
    }
  }
`;

// "Stock & verify": stock the quarantined unit and verify it, in one transaction. Returns the list,
// like every Verify write.
const SALES_ORDER_VERIFY_STOCK_QUARANTINED = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyStockQuarantined(
    $companyId: ID!
    $salesOrderId: ID!
    $input: SalesOrderVerifyStockQuarantinedInput!
  ) {
    salesOrderVerifyStockQuarantined(
      companyId: $companyId
      salesOrderId: $salesOrderId
      input: $input
    ) {
      lineKey
      addedQuantity
      removedQuantity
      stockedQuantity
      stockZone {
        id
        name
      }
      list {
        ...SalesOrderVerifyListFields
      }
    }
  }
`;

const SALES_ORDER_VERIFY_COMPLETE = gql`
  mutation SalesOrderVerifyComplete($companyId: ID!, $salesOrderId: ID!) {
    salesOrderVerifyComplete(companyId: $companyId, salesOrderId: $salesOrderId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      # A pickup order is set to Customer Pickup by this very request (or, refused, says why).
      customerPickup {
        enabled
        markedAt
        lastError
      }
    }
  }
`;

const SALES_ORDER_VERIFY_REOPEN = gql`
  ${VERIFY_LIST_FIELDS}
  mutation SalesOrderVerifyReopen($companyId: ID!, $salesOrderId: ID!) {
    salesOrderVerifyReopen(companyId: $companyId, salesOrderId: $salesOrderId) {
      ...SalesOrderVerifyListFields
    }
  }
`;

const SALES_ORDER_PICK_REOPEN = gql`
  mutation SalesOrderPickReopen($companyId: ID!, $salesOrderId: ID!) {
    salesOrderPickReopen(companyId: $companyId, salesOrderId: $salesOrderId) {
      salesOrderId
      vendorStatus
    }
  }
`;

// The EXISTING picking split, made Option-2 aware server-side: at Verify it splits the verified
// units off (that child is stamped verified and gets its own pack documents) and sends the rest
// back to Pick as a new, unstamped order. It returns the verified child.
const SPLIT_ORDER_AT_PICKING = gql`
  mutation SplitOrderAtPickingVerify($salesOrderId: ID!, $vendorId: ID!) {
    splitOrderAtPicking(salesOrderId: $salesOrderId, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      customerPickup {
        enabled
        lastError
      }
    }
  }
`;

/** Where an order-level refusal sends the operator: back to Pick, or the list. */
const leaveFor = (
  kind: ReturnType<typeof classifyVerifyOrderRefusal>,
  salesOrderId: string
) => (kind === 'pick' ? salesOrderPickPath(salesOrderId) : '/sales-orders');

function SalesOrderVerifyData() {
  const { id } = useParams();
  const vendorId = useRootStore((state) => state.selectedCompany.id) as string;
  const salesOrderId = id as string;

  const { data, error, loading, refetch } = useQuery(SALES_ORDER_VERIFY_LIST, {
    fetchPolicy: 'network-only',
    variables: { companyId: vendorId, salesOrderId },
    skip: !salesOrderId || !vendorId
  });
  const [scanMutation] = useMutation(SALES_ORDER_VERIFY_SCAN);
  const [removeMutation] = useMutation(SALES_ORDER_VERIFY_REMOVE);
  const [scanWithIdMutation] = useMutation(SALES_ORDER_VERIFY_SCAN_WITH_ID);
  const [removeWithIdMutation] = useMutation(SALES_ORDER_VERIFY_REMOVE_WITH_ID);
  const [scanBatchMutation] = useMutation(SALES_ORDER_VERIFY_SCAN_BATCH);
  const [completeMutation] = useMutation(SALES_ORDER_VERIFY_COMPLETE);
  const [reopenVerifyMutation] = useMutation(SALES_ORDER_VERIFY_REOPEN);
  const [reopenPickMutation] = useMutation(SALES_ORDER_PICK_REOPEN);
  const [splitMutation] = useMutation(SPLIT_ORDER_AT_PICKING);
  const [stockQuarantinedMutation] = useMutation(SALES_ORDER_VERIFY_STOCK_QUARANTINED);
  const apolloClient = useApolloClient();

  // Render from the last good list: a refetch the screen asks for after an unexplained failure
  // (errorPolicy 'none' clears `data`) must not swap the whole screen for an error page mid-order.
  // Never ACROSS orders — after a split the route moves to the verified child on this same element.
  const lastListRef = useRef<any>(null);
  if (
    lastListRef.current &&
    String(lastListRef.current.salesOrderId) !== String(salesOrderId)
  ) {
    lastListRef.current = null;
  }
  if (data?.salesOrderVerifyList) lastListRef.current = data.salesOrderVerifyList;
  const list = data?.salesOrderVerifyList ?? lastListRef.current;
  const screen = list ? verifyScreenFor(list) : null;
  // A refusal about the ORDER (not picking any more, not Option 2, back in Pick, gone) wins over any
  // list still on screen.
  const refusal = error ? classifyVerifyOrderRefusal(error) : null;

  // The load guards (§7.3 / §11.8): Pick stage → the Pick screen; not picking / shipping, or not an
  // Option-2 order at all → the list, saying why. Alerted once per order — the query re-renders.
  const alertedForRef = useRef<string | null>(null);
  const leaving = !!refusal || (!!list && (screen === 'pick' || screen === 'lost'));
  useEffect(() => {
    if (!leaving || alertedForRef.current === salesOrderId) return;
    alertedForRef.current = salesOrderId;
    if (refusal === 'pick' || (!refusal && screen === 'pick')) return; // straight to Pick
    const message =
      refusal === 'notFound'
        ? VERIFY_ORDER_NOT_FOUND_MESSAGE
        : refusal
        ? operatorFacingMessage(error, VERIFY_ORDER_NOT_FOUND_MESSAGE)
        : verifyLostOrderMessage(list);
    // Replaces the Apollo error link's 50-character toast (one Swal at a time) with the whole text.
    alert('Not on the Verify screen', message, 8, 'warning');
  }, [leaving, salesOrderId, list, screen, refusal, error]);

  if (!salesOrderId) return <Navigate to="/sales-orders" replace />;
  if (refusal) return <Navigate to={leaveFor(refusal, salesOrderId)} replace />;
  if (loading && !list) return <Spinner />;
  if (!list && error) return <ApiError error={error} />;
  if (!list) return <Spinner />;
  if (screen === 'pick')
    return <Navigate to={salesOrderPickPath(salesOrderId)} replace />;
  if (screen === 'lost') return <Navigate to="/sales-orders" replace />;
  // A verified pickup order waiting at Shipping has no Verify documents and no Ship step: its
  // summary records the pickup.
  const customerPickup = data?.salesOrder?.customerPickup ?? null;
  if (screen === 'documents' && customerPickup?.enabled)
    return <Navigate to={`/sales-order-summary/${salesOrderId}`} replace />;

  return (
    <SalesOrderVerify
      // A new order id (the verified child after "Short — split order") is a new screen: its own
      // queue, its own camera window, its own documents panel.
      key={salesOrderId}
      salesOrderId={salesOrderId}
      companyId={vendorId}
      initialList={list}
      company={data?.company || null}
      customerPickup={customerPickup}
      refetchList={async () => {
        const result = await refetch();
        return result?.data?.salesOrderVerifyList || null;
      }}
      scanMutation={scanMutation}
      removeMutation={removeMutation}
      scanWithIdMutation={scanWithIdMutation}
      removeWithIdMutation={removeWithIdMutation}
      scanBatchMutation={scanBatchMutation}
      completeMutation={completeMutation}
      reopenVerifyMutation={reopenVerifyMutation}
      reopenPickMutation={reopenPickMutation}
      splitMutation={splitMutation}
      quarantineQuery={({ variables }) =>
        apolloClient.query({
          query: SALES_ORDER_VERIFY_QUARANTINE,
          variables,
          fetchPolicy: 'network-only'
        })
      }
      stockQuarantinedMutation={stockQuarantinedMutation}
    />
  );
}

export default SalesOrderVerifyData;
